import { toast } from "sonner";

const SHOPIFY_API_VERSION = '2025-07';
const SHOPIFY_STORE_PERMANENT_DOMAIN = 'el-faza-stickers-mool1.myshopify.com';
const SHOPIFY_STOREFRONT_URL = `https://${SHOPIFY_STORE_PERMANENT_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`;
const SHOPIFY_STOREFRONT_TOKEN = import.meta.env.VITE_SHOPIFY_STOREFRONT_TOKEN || 'ae9ecc253b218cadce6809c071c69cac';

export interface ShopifyProduct {
  node: {
    id: string;
    title: string;
    description: string;
    handle: string;
    tags?: string[];
    productType?: string;
    collections?: {
      edges: Array<{ node: { handle: string; title: string } }>;
    };
    createdAt?: string;
    priceRange: {
      minVariantPrice: {
        amount: string;
        currencyCode: string;
      };
    };
    images: {
      edges: Array<{
        node: {
          url: string;
          altText: string | null;
        };
      }>;
    };
    variants: {
      edges: Array<{
        node: {
          id: string;
          title: string;
          price: {
            amount: string;
            currencyCode: string;
          };
          availableForSale: boolean;
          selectedOptions: Array<{
            name: string;
            value: string;
          }>;
        };
      }>;
    };
    options: Array<{
      name: string;
      values: string[];
    }>;
  };
}

export async function storefrontApiRequest(query: string, variables: Record<string, unknown> = {}) {
  const response = await fetch(SHOPIFY_STOREFRONT_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': SHOPIFY_STOREFRONT_TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });

  if (response.status === 402) {
    toast.error("Shopify: Payment required", {
      description: "Your store needs to be upgraded to a paid plan.",
    });
    return;
  }

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();

  if (data.errors) {
    throw new Error(`Error calling Shopify: ${data.errors.map((e: { message: string }) => e.message).join(', ')}`);
  }

  return data;
}

export const STOREFRONT_QUERY = `
  query GetProducts($first: Int!, $query: String) {
    products(first: $first, query: $query) {
      edges {
        node {
          id
          title
          description
          handle
          tags
          productType
          createdAt
          collections(first: 5) {
            edges { node { handle title } }
          }
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          images(first: 5) {
            edges {
              node {
                url
                altText
              }
            }
          }
          variants(first: 10) {
            edges {
              node {
                id
                title
                price {
                  amount
                  currencyCode
                }
                availableForSale
                selectedOptions {
                  name
                  value
                }
              }
            }
          }
          options {
            name
            values
          }
        }
      }
    }
  }
`;

export const PRODUCT_BY_HANDLE_QUERY = `
  query GetProductByHandle($handle: String!) {
    productByHandle(handle: $handle) {
      id
      title
      description
      handle
      tags
      productType
      createdAt
      collections(first: 5) {
        edges { node { handle title } }
      }
      priceRange {
        minVariantPrice {
          amount
          currencyCode
        }
      }
      images(first: 5) {
        edges {
          node {
            url
            altText
          }
        }
      }
      variants(first: 10) {
        edges {
          node {
            id
            title
            price {
              amount
              currencyCode
            }
            availableForSale
            selectedOptions {
              name
              value
            }
          }
        }
      }
      options {
        name
        values
      }
    }
  }
`;

// Cart mutations
export const CART_QUERY = `
  query cart($id: ID!) {
    cart(id: $id) { id totalQuantity }
  }
`;

export const CART_CREATE_MUTATION = `
  mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        lines(first: 100) { edges { node { id merchandise { ... on ProductVariant { id } } } } }
      }
      userErrors { field message }
    }
  }
`;

export const CART_LINES_ADD_MUTATION = `
  mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        id
        lines(first: 100) { edges { node { id merchandise { ... on ProductVariant { id } } } } }
      }
      userErrors { field message }
    }
  }
`;

export const CART_LINES_UPDATE_MUTATION = `
  mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart { id }
      userErrors { field message }
    }
  }
`;

export const CART_LINES_REMOVE_MUTATION = `
  mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart { id }
      userErrors { field message }
    }
  }
`;

export const CART_DISCOUNT_CODES_UPDATE_MUTATION = `
  mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]!) {
    cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
      cart {
        id
        checkoutUrl
      }
      userErrors { field message }
    }
  }
`;

function formatCheckoutUrl(checkoutUrl: string): string {
  try {
    const url = new URL(checkoutUrl);
    // Force checkout to go through the Shopify-hosted domain.
    // The custom domain (www.elfazaa.com) points to Lovable, not Shopify,
    // so checkout URLs on that host return 404. The myshopify.com domain
    // is always served by Shopify and handles /cart/c/... and /checkouts/... routes.
    url.hostname = SHOPIFY_STORE_PERMANENT_DOMAIN;
    url.searchParams.set('channel', 'online_store');
    return url.toString();
  } catch {
    return checkoutUrl;
  }
}

function isCartNotFoundError(userErrors: Array<{ field: string[] | null; message: string }>): boolean {
  return userErrors.some(e => e.message.toLowerCase().includes('cart not found') || e.message.toLowerCase().includes('does not exist'));
}

export type ProductType = 'sticker' | 'donation' | 'tip';

function getLineItemTypeLabel(productType: ProductType): string {
  switch (productType) {
    case 'donation':
      return 'Don solidaire';
    case 'tip':
      return 'Pourboire boutique';
    default:
      return 'Adhésif';
  }
}

export interface CartItem {
  lineId: string | null;
  product: ShopifyProduct;
  variantId: string;
  variantTitle: string;
  price: { amount: string; currencyCode: string };
  quantity: number;
  selectedOptions: Array<{ name: string; value: string }>;
  productType: ProductType;
}

export async function createShopifyCart(item: CartItem): Promise<{ cartId: string; checkoutUrl: string; lineId: string } | null> {
  const data = await storefrontApiRequest(CART_CREATE_MUTATION, {
    input: { lines: [{ quantity: item.quantity, merchandiseId: item.variantId, attributes: [{ key: "Type", value: getLineItemTypeLabel(item.productType) }] }] },
  });

  if (data?.data?.cartCreate?.userErrors?.length > 0) {
    console.error('Cart creation failed:', data.data.cartCreate.userErrors);
    return null;
  }

  const cart = data?.data?.cartCreate?.cart;
  if (!cart?.checkoutUrl) return null;

  const lineId = cart.lines.edges[0]?.node?.id;
  if (!lineId) return null;

  return { cartId: cart.id, checkoutUrl: formatCheckoutUrl(cart.checkoutUrl), lineId };
}

export async function addLineToShopifyCart(cartId: string, item: CartItem): Promise<{ success: boolean; lineId?: string; cartNotFound?: boolean }> {
  const data = await storefrontApiRequest(CART_LINES_ADD_MUTATION, {
    cartId,
    lines: [{ quantity: item.quantity, merchandiseId: item.variantId, attributes: [{ key: "Type", value: getLineItemTypeLabel(item.productType) }] }],
  });

  const userErrors = data?.data?.cartLinesAdd?.userErrors || [];
  if (isCartNotFoundError(userErrors)) return { success: false, cartNotFound: true };
  if (userErrors.length > 0) return { success: false };

  const lines = data?.data?.cartLinesAdd?.cart?.lines?.edges || [];
  const newLine = lines.find((l: { node: { id: string; merchandise: { id: string } } }) => l.node.merchandise.id === item.variantId);
  return { success: true, lineId: newLine?.node?.id };
}

export async function updateShopifyCartLine(cartId: string, lineId: string, quantity: number): Promise<{ success: boolean; cartNotFound?: boolean }> {
  const data = await storefrontApiRequest(CART_LINES_UPDATE_MUTATION, {
    cartId,
    lines: [{ id: lineId, quantity }],
  });

  const userErrors = data?.data?.cartLinesUpdate?.userErrors || [];
  if (isCartNotFoundError(userErrors)) return { success: false, cartNotFound: true };
  if (userErrors.length > 0) return { success: false };
  return { success: true };
}

export async function removeLineFromShopifyCart(cartId: string, lineId: string): Promise<{ success: boolean; cartNotFound?: boolean }> {
  const data = await storefrontApiRequest(CART_LINES_REMOVE_MUTATION, {
    cartId,
    lineIds: [lineId],
  });

  const userErrors = data?.data?.cartLinesRemove?.userErrors || [];
  if (isCartNotFoundError(userErrors)) return { success: false, cartNotFound: true };
  if (userErrors.length > 0) return { success: false };
  return { success: true };
}

export async function applyDiscountCodesToCart(cartId: string, discountCodes: string[]): Promise<{ success: boolean; checkoutUrl?: string }> {
  const data = await storefrontApiRequest(CART_DISCOUNT_CODES_UPDATE_MUTATION, {
    cartId,
    discountCodes,
  });

  const userErrors = data?.data?.cartDiscountCodesUpdate?.userErrors || [];
  if (userErrors.length > 0) {
    console.error('Discount code apply failed:', userErrors);
    return { success: false };
  }

  const checkoutUrl = data?.data?.cartDiscountCodesUpdate?.cart?.checkoutUrl;

  return {
    success: true,
    checkoutUrl: checkoutUrl ? formatCheckoutUrl(checkoutUrl) : undefined,
  };
}

// Pourboire (tip) variant — 1 $ par unité. Quantity = montant arrondi à l'entier.
// Variant "1$" du produit "Don Solidaire" (id 7515970928737).
// Quantity = montant en dollars (ex: 5 = 5$). NE PAS modifier sans recréer un variant à 1$.
export const TIP_VARIANT_ID = 'gid://shopify/ProductVariant/44042448765025';

const CART_LINES_QUERY = `
  query cartLines($id: ID!) {
    cart(id: $id) {
      id
      lines(first: 100) {
        edges {
          node {
            id
            quantity
            merchandise { ... on ProductVariant { id } }
          }
        }
      }
    }
  }
`;

/**
 * Synchronise la ligne pourboire dans le panier Shopify.
 * - tipAmount > 0 : ajoute ou met à jour (quantity = round(tipAmount)).
 * - tipAmount <= 0 : retire la ligne pourboire si présente.
 */
export async function syncTipLine(cartId: string, tipAmount: number): Promise<{ success: boolean }> {
  const desiredQty = Math.max(0, Math.round(tipAmount));

  let existingLineId: string | null = null;
  try {
    const data = await storefrontApiRequest(CART_LINES_QUERY, { id: cartId });
    const edges = data?.data?.cart?.lines?.edges || [];
    const found = edges.find((e: { node: { merchandise: { id?: string } } }) => e.node.merchandise?.id === TIP_VARIANT_ID);
    existingLineId = found?.node?.id ?? null;
  } catch (error) {
    console.error('Tip line lookup failed:', error);
    return { success: false };
  }

  if (desiredQty === 0) {
    if (!existingLineId) return { success: true };
    const result = await removeLineFromShopifyCart(cartId, existingLineId);
    return { success: result.success };
  }

  if (existingLineId) {
    const result = await updateShopifyCartLine(cartId, existingLineId, desiredQty);
    return { success: result.success };
  }

  const result = await addLineToShopifyCart(cartId, {
    lineId: null,
    product: {} as ShopifyProduct,
    variantId: TIP_VARIANT_ID,
    variantTitle: 'Pourboire',
    price: { amount: '1.00', currencyCode: 'CAD' },
    quantity: desiredQty,
    selectedOptions: [],
    productType: 'tip',
  });
  return { success: result.success };
}
